import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { EnhancedTaskItemView } from './EnhancedTaskItem';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    enhancedTaskItem: {
      /**
       * Toggle a task item
       */
      toggleTaskItem: () => ReturnType,
    }
  }
}

export const EnhancedTaskItemExtension = Node.create({
  name: 'taskItem',

  priority: 51,

  addOptions() {
    return {
      nested: false,
      HTMLAttributes: {},
    }
  },

  group: 'listItem',

  content() {
    return this.options.nested ? 'paragraph block*' : 'paragraph+'
  },

  defining: true,

  addAttributes() {
    return {
      checked: {
        default: false,
        keepOnSplit: false,
        parseHTML: element => element.getAttribute('data-checked') === 'true',
        renderHTML: attributes => ({
          'data-checked': attributes.checked,
        }),
      },
      completedBy: {
        default: null,
        keepOnSplit: false,
        parseHTML: element => element.getAttribute('data-completed-by'),
        renderHTML: attributes => {
          if (!attributes.completedBy) {
            return {};
          }
          return {
            'data-completed-by': attributes.completedBy,
          };
        },
      },
      completedAt: {
        default: null,
        keepOnSplit: false,
        parseHTML: element => element.getAttribute('data-completed-at'),
        renderHTML: attributes => {
          if (!attributes.completedAt) {
            return {};
          }
          return {
            'data-completed-at': attributes.completedAt,
          };
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `li[data-type="${this.name}"]`,
        priority: 51,
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'li',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-type': this.name,
      }),
      [
        'label',
        [
          'input',
          {
            type: 'checkbox',
            checked: node.attrs.checked ? 'checked' : null,
          },
        ],
      ],
      ['div', 0],
    ]
  },

  addKeyboardShortcuts() {
    const shortcuts = {
      Enter: () => this.editor.commands.splitListItem(this.name),
      'Shift-Tab': () => this.editor.commands.liftListItem(this.name),
    }

    if (!this.options.nested) {
      return shortcuts
    }

    return {
      ...shortcuts,
      Tab: () => this.editor.commands.sinkListItem(this.name),
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(EnhancedTaskItemView)
  },

  addCommands() {
    return {
      toggleTaskItem: () => ({ commands }) => {
        return commands.toggleNode(this.name, 'paragraph')
      },
    }
  },
})